import { Box, Stack } from "@mui/material";
import type { KeyboardEvent, ReactNode } from "react";
import { useTheme } from "@mui/material/styles";

import { ReferencePagination } from "./ReferencePagination";

export type SelectionGridColumn<T> = {
    id: string;
    render: (row: T) => ReactNode;
    width?: number | string;
};

type SelectionGridProps<T> = {
    columns: SelectionGridColumn<T>[];
    emptyContent: ReactNode;
    getRowId: (row: T) => string;
    getRowAriaLabel?: (row: T) => string;
    isLoading: boolean;
    isRowDisabled?: (row: T) => boolean;
    isRowSelected?: (row: T) => boolean;
    onPageChange: (page: number) => void;
    onSelect: (row: T) => void;
    page: number;
    pageCount: number;
    rows: T[];
    maxVisibleRows?: number;
    minTableWidth?: number;
};

export const SelectionGrid = <T,>(props: SelectionGridProps<T>) => {
    const {
        columns,
        emptyContent,
        getRowId,
        getRowAriaLabel,
        isLoading,
        isRowDisabled = () => false,
        isRowSelected = () => false,
        onPageChange,
        onSelect,
        page,
        pageCount,
        rows,
        maxVisibleRows = 7,
        minTableWidth = 736,
    } = props;
    const theme = useTheme();
    const divider = `${theme.custom.strokes.containerDivider.width} ${theme.custom.strokes.containerDivider.style} ${theme.custom.strokes.containerDivider.color}`;
    const visibleRowLimit = Math.max(1, Math.floor(maxVisibleRows));
    const visibleRowCount = Math.max(1, Math.min(rows.length, visibleRowLimit));
    const viewportHeight = theme.spacing(visibleRowCount * 8);
    const viewportMaxHeight = theme.spacing(visibleRowLimit * 8);

    const selectRow = (row: T) => {
        if (!isRowDisabled(row)) {
            onSelect(row);
        }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, row: T) => {
        if (event.key !== "Enter" && event.key !== " ") return;

        event.preventDefault();
        selectRow(row);
    };

    return (
        <Stack data-testid="selection-grid" flex="0 1 auto" minHeight={0} width="100%" sx={{ borderTop: divider }}>
            <Box
                data-testid="selection-grid-viewport"
                sx={{
                    flexShrink: 1,
                    height: viewportHeight,
                    maxHeight: viewportMaxHeight,
                    minHeight: theme.spacing(8),
                    overflowX: "auto",
                    overflowY: "auto",
                    position: "relative",
                    width: "100%",
                }}
            >
                {isLoading && (
                    <Box
                        aria-label="Loading..."
                        role="status"
                        sx={{
                            clip: "rect(0 0 0 0)",
                            clipPath: "inset(50%)",
                            height: 1,
                            overflow: "hidden",
                            position: "absolute",
                            whiteSpace: "nowrap",
                            width: 1,
                        }}
                    />
                )}
                <Box
                    aria-busy={isLoading}
                    component="table"
                    sx={{
                        borderCollapse: "collapse",
                        minWidth: `${minTableWidth}px`,
                        tableLayout: "fixed",
                        width: "100%",
                    }}
                >
                    <Box component="tbody">
                        {isLoading ?
                            Array.from({ length: visibleRowCount }, (_value, rowIndex) => (
                                <Box
                                    component="tr"
                                    key={`selection-grid-skeleton-${rowIndex}`}
                                    sx={{ height: theme.spacing(8) }}
                                >
                                    {columns.map((column, columnIndex) => (
                                        <Box
                                            component="td"
                                            key={column.id}
                                            sx={{
                                                borderBottom: divider,
                                                boxSizing: "border-box",
                                                height: theme.spacing(8),
                                                padding: theme.spacing(1, 1.5),
                                                verticalAlign: "middle",
                                                width:
                                                    typeof column.width === "number" ?
                                                        `${column.width}px`
                                                    :   (column.width ?? `${100 / columns.length}%`),
                                            }}
                                        >
                                            <Box
                                                data-testid="selection-grid-skeleton-cell"
                                                sx={{
                                                    animation: `selection-grid-skeleton ${theme.transitions.duration.complex}ms ${theme.transitions.easing.easeInOut} infinite`,
                                                    backgroundImage: `linear-gradient(90deg, ${theme.custom.grey[300]} 0%, ${theme.custom.grey[500]} 50%, ${theme.custom.grey[300]} 100%)`,
                                                    backgroundSize: "200% 100%",
                                                    borderRadius: theme.spacing(0.5),
                                                    height: theme.spacing(2),
                                                    maxWidth: theme.spacing(columnIndex === 0 ? 26 : 20),
                                                    width: columnIndex === 0 ? "72%" : "56%",
                                                    "@keyframes selection-grid-skeleton": {
                                                        from: { backgroundPosition: "200% 0" },
                                                        to: { backgroundPosition: "-200% 0" },
                                                    },
                                                    "@media (prefers-reduced-motion: reduce)": {
                                                        animation: "none",
                                                    },
                                                }}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            ))
                        : rows.length > 0 ?
                            rows.map((row) => {
                                const disabled = isRowDisabled(row);
                                const selected = isRowSelected(row);

                                return (
                                    <Box
                                        aria-disabled={disabled || undefined}
                                        aria-label={getRowAriaLabel?.(row)}
                                        aria-pressed={selected}
                                        component="tr"
                                        key={getRowId(row)}
                                        onClick={() => selectRow(row)}
                                        onKeyDown={(event: KeyboardEvent<HTMLTableRowElement>) =>
                                            handleKeyDown(event, row)
                                        }
                                        role="button"
                                        tabIndex={disabled ? -1 : 0}
                                        sx={{
                                            backgroundColor: selected ? theme.palette.action.selected : "transparent",
                                            cursor: disabled ? "default" : "pointer",
                                            height: theme.spacing(8),
                                            opacity: disabled ? 0.55 : 1,
                                            ...(!disabled && {
                                                "&:hover": {
                                                    backgroundColor:
                                                        selected ?
                                                            theme.palette.action.selected
                                                        :   theme.palette.action.hover,
                                                },
                                            }),
                                            "&:last-child td": { borderBottom: divider },
                                        }}
                                    >
                                        {columns.map((column) => (
                                            <Box
                                                component="td"
                                                key={column.id}
                                                sx={{
                                                    borderBottom: divider,
                                                    boxSizing: "border-box",
                                                    height: theme.spacing(8),
                                                    padding: theme.spacing(1, 1.5),
                                                    verticalAlign: "middle",
                                                    width:
                                                        typeof column.width === "number" ?
                                                            `${column.width}px`
                                                        :   (column.width ?? `${100 / columns.length}%`),
                                                }}
                                            >
                                                {column.render(row)}
                                            </Box>
                                        ))}
                                    </Box>
                                );
                            })
                        :   <Box component="tr" sx={{ height: theme.spacing(8) }}>
                                <Box
                                    component="td"
                                    colSpan={columns.length}
                                    sx={{ borderBottom: divider, padding: theme.spacing(2) }}
                                >
                                    {emptyContent}
                                </Box>
                            </Box>
                        }
                    </Box>
                </Box>
            </Box>

            {pageCount > 0 && (
                <Stack
                    data-testid="selection-grid-pagination"
                    alignItems="center"
                    direction="row"
                    flexShrink={0}
                    height={theme.spacing(9.5)}
                    justifyContent="flex-end"
                    paddingX={theme.spacing(2)}
                    sx={{ borderTop: divider, boxSizing: "border-box" }}
                >
                    <ReferencePagination
                        disabled={isLoading}
                        onPageChange={onPageChange}
                        page={page}
                        pageCount={pageCount}
                    />
                </Stack>
            )}
        </Stack>
    );
};
